import {isPlainObject, isString, isUndefined} from 'lodash';
import log from 'loglevel';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {isDevelopment, isProduction} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {datadogLogger} from './datadogLogger';
import {isDatadogStatus, LogLevelsEnum, LogMethod} from './loggerCommon';

/*
 * Constants.
 */

const isDatadogEnabledInDevelopment = false;
const datadogClientTokenDev = 'pub150b2230647bd4475b8ea32144d22455';
const datadogClientTokenProd = 'pubd8d3feab5e9c7bb46a1189d01aa8cb50';

const shouldEnableDevLogs = isDevelopment() && !isDatadogEnabledInDevelopment;
export const defaultLogLevel = shouldEnableDevLogs ? LogLevelsEnum.DEBUG : LogLevelsEnum.INFO;

/*
 * Types.
 */

declare global {
  interface Window {
    frontLog: DefaultLogger;
  }
}

interface DefaultLogger extends Logger {
  getLogger(name: string): Logger;
  getLoggers(): {[name: string]: Logger};
}

interface Logger {
  debug: LogMethod;
  info: LogMethod;
  warn: LogMethod;
  error: LogMethod;

  setDefaultLevel(level: LogLevelsEnum): void;
  setLevel(level: LogLevelsEnum, persist?: boolean): void;
}

export interface LoggerGlobalContext {
  region?: string;
  cell?: string;
  companyId: number;
  userId: number;
}

/*
 * State.
 */

let isDatadogInitialized = false;

/*
 * Setup.
 */

const originalMethodFactory = log.methodFactory;
log.methodFactory = (methodName, logLevel, loggerName) => {
  const originalMethod = originalMethodFactory(methodName, logLevel, loggerName);

  if (!isDatadogStatus(methodName)) {
    return originalMethod;
  }

  return (...args) => {
    originalMethod(...args);
    if (!isDatadogInitialized) {
      return;
    }

    const message = args.shift();
    if (!isString(message)) {
      return;
    }

    const context = args.shift() || {};
    if (!isPlainObject(context)) {
      return;
    }

    datadogLogger.log(message, context, methodName, loggerName);
  };
};
log.setDefaultLevel(defaultLogLevel);

/*
 * API.
 */

// Expose the default logger as a global.
globalWindow.frontLog = log;

// Export the default loggers.
export const logger: DefaultLogger = log;

export function setupDesktopDatadogLogger() {
  if (isDatadogInitialized) {
    return;
  }

  const isDatadogEnabled = isDevelopment() ? isDatadogEnabledInDevelopment : true;
  if (!isDatadogEnabled) {
    return;
  }

  datadogLogger.initDesktop({
    clientToken: isProduction() ? datadogClientTokenProd : datadogClientTokenDev,
    // Because we have Bugsnag for that.
    forwardErrorsToLogs: false,
  });

  datadogLogger.setClientAttributes();

  isDatadogInitialized = true;
}

export async function setupMobileDatadogLoggerAsync() {
  if (isDatadogInitialized) {
    return;
  }

  const isDatadogEnabled = isDevelopment() ? isDatadogEnabledInDevelopment : true;
  if (!isDatadogEnabled) {
    return;
  }

  await datadogLogger.initMobileAsync({
    clientToken: isProduction() ? datadogClientTokenProd : datadogClientTokenDev,
    // Because we have Bugsnag for that.
    forwardErrorsToLogs: false,
  });

  datadogLogger.setClientAttributes();

  isDatadogInitialized = true;
}

export function registerDatadogContext(context: LoggerGlobalContext) {
  const {region, cell, companyId, userId} = context;

  if (!isDatadogInitialized) {
    return;
  }

  if (!isUndefined(region)) {
    datadogLogger.addLoggerGlobalContext('region', region);
  }

  if (!isUndefined(cell)) {
    datadogLogger.addLoggerGlobalContext('cell', cell);
  }

  datadogLogger.addLoggerGlobalContext('company_id', companyId);
  datadogLogger.addLoggerGlobalContext('user_id', userId);
}
